import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Megaphone, ArrowRight, Sparkle, Clock } from '@phosphor-icons/react';

const DURATIONS = [
  { hours: 24, label: '24 hours' },
  { hours: 48, label: '2 days' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '1 week' },
];

const Sponsor = () => {
  const [pricing, setPricing] = useState({ tiers: [] });
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    cta_label: 'Open',
    cta_url: '',
    image_prompt: '',
    duration_hours: 24,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/sponsor/pricing').then(({ data }) => setPricing(data)).catch(() => {});
    api.get('/sponsor/mine').then(({ data }) => setMine(data.ads)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedTier = pricing.tiers.find((t) => t.hours === form.duration_hours) ||
    pricing.tiers[0] || { usd: 49 };

  const submit = async () => {
    if (!form.title.trim() || !form.cta_url.trim() || !form.image_prompt.trim()) {
      toast.error('title, CTA URL, and image prompt required'); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/sponsor/checkout', {
        ...form,
        duration_hours: parseInt(form.duration_hours, 10),
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'checkout failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-10">
        <Megaphone size={36} weight="duotone" className="text-bx-volt mx-auto" />
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">
          Promote your tribe. <span className="shimmer-text">In the feed.</span>
        </h1>
        <p className="text-white/65 mt-2 max-w-2xl mx-auto">
          One sentence in, fully-generated AI banner out. Your sponsored slot lands directly inside the BubbleX feed for the next 24 hours and up.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 glass rounded-2xl p-6 space-y-4">
          <h2 className="font-display text-xl font-bold mb-2">Build your sponsor slot</h2>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Headline</span>
            <input data-testid="sponsor-title" value={form.title} onChange={set('title')} placeholder="e.g. The Houston Music Tribe just dropped." maxLength={80}
              className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Subtitle</span>
            <input data-testid="sponsor-subtitle" value={form.subtitle} onChange={set('subtitle')} placeholder="One line of context." maxLength={140}
              className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">CTA label</span>
              <input data-testid="sponsor-cta-label" value={form.cta_label} onChange={set('cta_label')} maxLength={24}
                className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">CTA URL</span>
              <input data-testid="sponsor-cta-url" value={form.cta_url} onChange={set('cta_url')} placeholder="https://"
                className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">AI image prompt</span>
            <textarea data-testid="sponsor-prompt" value={form.image_prompt} onChange={set('image_prompt')} rows={2} maxLength={240}
              placeholder="What does the banner feel like? e.g. neon Houston skyline at midnight, music tribe energy"
              className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm resize-none" />
            <p className="text-[11px] text-white/60 mt-1">Generated by Nano Banana right after payment. Auto-moderated.</p>
          </label>

          <div>
            <span className="text-xs uppercase tracking-widest text-white/65">Duration</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {DURATIONS.map((d) => {
                const tier = pricing.tiers.find((t) => t.hours === d.hours);
                return (
                  <button
                    key={d.hours}
                    data-testid={`sponsor-tier-${d.hours}`}
                    onClick={() => setForm({ ...form, duration_hours: d.hours })}
                    className={`px-4 py-2.5 rounded-xl border text-sm font-bold flex flex-col items-start transition ${
                      form.duration_hours === d.hours
                        ? 'border-bx-cyan bg-bx-cyan/10 text-bx-cyan'
                        : 'border-white/10 text-white/70 hover:bg-white/5'
                    }`}
                  >
                    <span>{d.label}</span>
                    <span className="text-[10px] opacity-70">${tier?.usd ?? '—'}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <button data-testid="sponsor-submit" onClick={submit} disabled={busy}
                  className="w-full btn-primary py-3.5 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50 mt-3">
            {busy ? 'Redirecting…' : <>Continue to checkout · ${selectedTier.usd} <ArrowRight size={14} weight="bold" /></>}
          </button>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-widest text-white/65 mb-3">Live preview</div>
            <div className="grad-border p-5 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full blur-3xl bg-bx-magenta/30 pointer-events-none" />
              <div className="absolute -bottom-16 -left-16 h-44 w-44 rounded-full blur-3xl bg-bx-cyan/30 pointer-events-none" />
              <div className="relative">
                <div className="chip text-bx-volt border-bx-volt/30 mb-3">
                  <Megaphone size={11} weight="fill" /> Sponsored
                </div>
                <div className="font-display text-2xl font-black tracking-tighter leading-tight">
                  {form.title || 'Your headline'}
                </div>
                {form.subtitle && <p className="text-white/65 mt-2 text-sm">{form.subtitle}</p>}
                <div className="btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1 mt-4">
                  {form.cta_label || 'Learn more'}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={18} weight="duotone" className="text-bx-cyan" />
              <h3 className="font-display text-lg font-bold">Your sponsored slots</h3>
            </div>
            {mine.length === 0 ? (
              <div className="text-white/60 text-sm">No active sponsors yet.</div>
            ) : (
              <div className="space-y-2">
                {mine.map((a) => (
                  <div key={a.id} className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`sponsor-mine-${a.id}`}>
                    <div className="font-bold text-sm truncate">{a.title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">
                      ${a.amount_paid} · until {a.active_until ? new Date(a.active_until).toLocaleString() : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-12 text-center text-xs text-white/60">
        <Sparkle size={12} weight="fill" className="inline text-bx-volt" />{' '}
        Payments via Stripe. All sponsor copy is content-moderated before generation.
      </div>
    </div>
  );
};

export default Sponsor;
